import { useEffect, useMemo, useRef, useState } from "react";
import { SearchIcon, PinIcon, CalendarIcon, UserIcon } from "./icons.jsx";
import { useSuggestions, islandSlug } from "../lib/search.js";
import { iso, parseISO, addDays, fmtShort } from "../lib/dates.js";
import {
  Panel, WherePanelContent, DatesPanelContent, GuestsPanelContent,
  useIsMobile, useScrollLock, makeDatePicker
} from "./SearchSheets.jsx";

export default function HeroSearch() {
  const today = useMemo(() => new Date(), []);
  const [where, setWhere] = useState("");
  const [selectedIsland, setSelectedIsland] = useState("");
  const [pickedPropertyId, setPickedPropertyId] = useState("");
  const [pickedPropertyIsland, setPickedPropertyIsland] = useState("");
  const initial = useMemo(() => new URLSearchParams(location.search), []);
  const [checkin, setCheckin] = useState(() => initial.get("checkin") || iso(addDays(today, 14)));
  const [checkout, setCheckout] = useState(() => initial.get("checkout") || iso(addDays(today, 17)));
  const [adults, setAdults] = useState(() => Math.max(1, Number(initial.get("adults")) || 2));
  const [childAges, setChildAges] = useState(() => (initial.get("children") || "").split(",").filter(Boolean).map(Number).filter(n => n >= 0 && n <= 17));
  const [open, setOpen] = useState("");   // "where" | "dates" | "guests" | ""
  const [phase, setPhase] = useState("start");
  const [hover, setHover] = useState("");
  const [calMonth, setCalMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const rootRef = useRef(null);
  const isMobile = useIsMobile();
  useScrollLock(isMobile && !!open);

  useEffect(() => {
    // The mobile sheet is portalled out of this subtree, so containment alone
    // would read a tap inside it as a tap outside and close it.
    const onDown = e => {
      if (e.target?.closest?.("[data-search-panel]")) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen("");
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const suggestions = useSuggestions(where, { enabled: open === "where" });

  const clearProperty = () => { setPickedPropertyId(""); setPickedPropertyIsland(""); };
  const pickIsland   = item  => { setWhere(item.display); setSelectedIsland(item.value); clearProperty(); setOpen(""); };
  const pickPlace    = place => { setWhere(place.area);  setSelectedIsland(place.island); clearProperty(); setOpen(""); };
  const pickProperty = prop  => { setWhere(prop.name);   setSelectedIsland(prop.island); setPickedPropertyId(prop.id); setPickedPropertyIsland(prop.island); setOpen(""); };
  const pickDate = makeDatePicker({
    checkin, phase, setPhase, setHover,
    onChange: (ci, co) => { setCheckin(ci); setCheckout(co); }
  });

  const submit = e => {
    e.preventDefault();
    const q = new URLSearchParams({
      checkin: checkin || iso(addDays(today, 14)),
      checkout: checkout || iso(addDays(today, 17)),
      adults: String(adults)
    });
    if (where.trim()) q.set("q", where.trim());
    const targetIsland = pickedPropertyIsland || selectedIsland;
    const slug = islandSlug(targetIsland);
    if (slug) q.set("island", slug);
    if (pickedPropertyId) q.set("pin", pickedPropertyId);
    if (childAges.length) q.set("children", childAges.join(","));
    window.location.href = `/results/?${q}`;
  };

  const dateLabel = (checkin && checkout) ? `${fmtShort(checkin)} — ${fmtShort(checkout)}` : "Select dates";
  const guestLabel = `${adults} adult${adults !== 1 ? "s" : ""}${childAges.length ? ` · ${childAges.length} child${childAges.length !== 1 ? "ren" : ""}` : ""}`;
  const fieldClass = active =>
    `flex-1 min-w-0 text-left rounded-[22px] px-4 py-3.5 md:px-5 md:py-3 transition ${active ? "bg-sand-100 ring-2 ring-ocean-200" : "hover:bg-sand-50"}`;
  const labelClass = "text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted";
  const valueClass = "text-base md:text-sm font-medium truncate";

  return (
    <div ref={rootRef} className="relative">
      <form onSubmit={submit} className="rounded-[28px] bg-white shadow-cardHover ring-1 ring-white/50 p-2 md:p-3 flex flex-col md:flex-row md:items-stretch gap-2">
        {/* WHERE */}
        <button type="button" onClick={() => setOpen(open === "where" ? "" : "where")} className={fieldClass(open === "where")}>
          <div className="flex items-center gap-3">
            <PinIcon size={20} className="text-ocean-600 shrink-0"/>
            <div className="min-w-0 flex-1">
              <div className={labelClass}>Where</div>
              <div className={`${valueClass} ${where ? "text-ink" : "text-ink-muted"}`}>{where || "Anywhere in Hawaiʻi"}</div>
            </div>
          </div>
        </button>

        <div className="hidden md:block w-px self-stretch bg-ink/10"/>

        {/* DATES */}
        <button type="button" onClick={() => { setOpen(open === "dates" ? "" : "dates"); setCalMonth(new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1)); setPhase(checkin && !checkout ? "end" : "start"); setHover(""); }}
          className={fieldClass(open === "dates")}>
          <div className="flex items-center gap-3">
            <CalendarIcon size={20} className="text-ocean-600 shrink-0"/>
            <div className="min-w-0 flex-1">
              <div className={labelClass}>When</div>
              <div className={`${valueClass} ${checkin && checkout ? "text-ink" : "text-ink-muted"}`}>{dateLabel}</div>
            </div>
          </div>
        </button>

        <div className="hidden md:block w-px self-stretch bg-ink/10"/>

        {/* GUESTS */}
        <button type="button" onClick={() => setOpen(open === "guests" ? "" : "guests")} className={fieldClass(open === "guests")}>
          <div className="flex items-center gap-3">
            <UserIcon size={20} className="text-ocean-600 shrink-0"/>
            <div className="min-w-0 flex-1">
              <div className={labelClass}>Who</div>
              <div className={`${valueClass} text-ink`}>{guestLabel}</div>
            </div>
          </div>
        </button>

        {/* SEARCH */}
        <button className="btn-sunset shrink-0 py-3.5 text-base md:py-3 md:px-7">
          <SearchIcon size={18}/> Search
        </button>
      </form>

      {open === "where" && (
        <Panel isMobile={isMobile} title="Where to?" onClose={() => setOpen("")} desktop="md:left-0 md:w-[520px] md:p-3" bodyClass="p-4 md:p-0">
          <WherePanelContent
            value={where}
            onChange={v => { setWhere(v); setSelectedIsland(""); clearProperty(); }}
            onClear={() => { setWhere(""); setSelectedIsland(""); clearProperty(); }}
            suggestions={suggestions}
            onIsland={pickIsland} onPlace={pickPlace} onProperty={pickProperty}
          />
        </Panel>
      )}

      {open === "dates" && (
        <Panel
          isMobile={isMobile}
          title="Select dates"
          onClose={() => setOpen("")}
          desktop="md:left-0 md:w-[720px] md:p-6"
          bodyClass="p-4 md:p-0"
          footerClass="md:px-6 md:pb-6 md:pt-4"
          footer={
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => { setCheckin(""); setCheckout(""); setPhase("start"); setHover(""); }} className="text-sm text-ink-muted hover:text-ink underline">Clear dates</button>
              <button type="button" onClick={() => setOpen("")} className="btn-primary py-3 px-8 text-sm md:py-2 md:px-6" disabled={!checkin || !checkout}>
                {checkin && checkout ? "Done" : "Select a check-out date"}
              </button>
            </div>
          }
        >
          <DatesPanelContent
            today={today} checkin={checkin} checkout={checkout}
            phase={phase} hover={hover} setHover={setHover} onPick={pickDate}
            calMonth={calMonth} setCalMonth={setCalMonth}
          />
        </Panel>
      )}

      {open === "guests" && (
        <Panel
          isMobile={isMobile}
          title="Travelers"
          onClose={() => setOpen("")}
          desktop="md:right-0 md:w-[360px] md:p-5"
          bodyClass="p-4 md:p-0"
          footerClass="md:px-5 md:pb-5 md:pt-4"
          footer={
            <button type="button" onClick={() => setOpen("")} className="btn-primary w-full py-3 text-base md:w-auto md:py-2 md:px-6 md:text-sm md:float-right">Done</button>
          }
        >
          <GuestsPanelContent adults={adults} setAdults={setAdults} childAges={childAges} setChildAges={setChildAges}/>
        </Panel>
      )}
    </div>
  );
}
